import React, { useState, useEffect } from 'react';
import { Container, Card, Button, Alert, Modal, Spinner, Badge, Form } from 'react-bootstrap';
import { useTranslation } from 'react-i18next';
import { ApiActions } from '../shared/apiActions';
import apiCall from '../shared/apiCall';
import SlidingMenu from '../components/SlidingMenu';
import VersionFooter from '../components/VersionFooter';

export default function InboxContactUs() {
  const { t } = useTranslation();
  const [contactMessages, setContactMessages] = useState([]);
  const [loadingContactMessages, setLoadingContactMessages] = useState(false);
  const [updatingMessageId, setUpdatingMessageId] = useState(null);
  const [deletingMessageId, setDeletingMessageId] = useState(null);
  const [showDeleteMessageModal, setShowDeleteMessageModal] = useState(false);
  const [messageToDelete, setMessageToDelete] = useState(null);
  const [statusFilter, setStatusFilter] = useState('new'); // Default filter: 'new'
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  useEffect(() => {
    fetchContactMessages();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [statusFilter]);

  const fetchContactMessages = async () => {
    setLoadingContactMessages(true);
    setError('');
    try {
      const response = await apiCall(
        `/api/get?action=${ApiActions.GET_CONTACT_MESSAGES}&status=${statusFilter}&limit=20`,
        {},
        'GET'
      );
      if (response.success && response.messages) {
        setContactMessages(response.messages);
      } else {
        setError(response.message || t('inbox.failedToLoadMessages'));
      }
    } catch (err) {
      console.error('Error fetching contact messages:', err);
      setError(t('inbox.errorLoadingMessages'));
    } finally {
      setLoadingContactMessages(false);
    }
  };

  const handleUpdateMessageStatus = async (messageId, newStatus) => {
    setUpdatingMessageId(messageId);
    try {
      const response = await apiCall(
        `/api/set?action=${ApiActions.UPDATE_CONTACT_MESSAGE_STATUS}`,
        {
          messageId: messageId,
          status: newStatus
        },
        'POST'
      );
      if (response.success) {
        // If the new status doesn't match the current filter, remove it from the list
        if (newStatus !== statusFilter) {
          setContactMessages(prevMessages =>
            prevMessages.filter(msg => msg.id !== messageId)
          );
        } else {
          // Update the message in the local state
          setContactMessages(prevMessages =>
            prevMessages.map(msg =>
              msg.id === messageId ? { ...msg, status: newStatus } : msg
            )
          );
        }
        setSuccess('Message status updated successfully.');
        setTimeout(() => setSuccess(''), 3000);
      } else {
        setError(response.message || 'Failed to update message status.');
        setTimeout(() => setError(''), 5000);
      }
    } catch (err) {
      console.error('Error updating message status:', err);
      setError('An error occurred while updating message status.');
      setTimeout(() => setError(''), 5000);
    } finally {
      setUpdatingMessageId(null);
    }
  };

  const handleDeleteMessage = async () => {
    if (!messageToDelete) return;
    
    setDeletingMessageId(messageToDelete.id);
    try {
      const response = await apiCall(
        `/api/set?action=${ApiActions.DELETE_CONTACT_MESSAGE}`,
        {
          messageId: messageToDelete.id
        },
        'POST'
      );
      if (response.success) {
        // Remove the message from the local state
        setContactMessages(prevMessages =>
          prevMessages.filter(msg => msg.id !== messageToDelete.id)
        );
        setSuccess('Message deleted successfully.');
        setTimeout(() => setSuccess(''), 3000);
        setShowDeleteMessageModal(false);
        setMessageToDelete(null);
      } else {
        setError(response.message || 'Failed to delete message.');
        setTimeout(() => setError(''), 5000);
      }
    } catch (err) {
      console.error('Error deleting message:', err);
      setError('An error occurred while deleting message.');
      setTimeout(() => setError(''), 5000);
    } finally {
      setDeletingMessageId(null);
    }
  };

  const handleOpenDeleteModal = (message) => {
    setMessageToDelete(message);
    setShowDeleteMessageModal(true);
  };

  return (
    <div className="inbox-contact-us-page">
      <SlidingMenu title="Inbox - Contact Us" />
      
      <Container className="py-4">
        <div className="mb-4">
          <h2 className="mb-0">
            <i className="bi bi-envelope me-2"></i>
            Contact Us Messages
          </h2>
          <p className="text-muted mb-0">Manage contact messages from users</p>
        </div>

        {success && (
          <Alert variant="success" className="mb-4" onClose={() => setSuccess('')} dismissible>
            <i className="bi bi-check-circle me-2"></i>
            {success}
          </Alert>
        )}

        {error && (
          <Alert variant="danger" className="mb-4" onClose={() => setError('')} dismissible>
            <i className="bi bi-exclamation-circle me-2"></i>
            {error}
          </Alert>
        )}

        {/* Status Filter */}
        <Card className="mb-4 shadow-sm">
          <Card.Body>
            <div className="d-flex align-items-center gap-3">
              <label className="mb-0 fw-bold">Filter by Status:</label>
              <Form.Select
                value={statusFilter}
                onChange={(e) => setStatusFilter(e.target.value)}
                style={{ width: 'auto', minWidth: '150px' }}
              >
                <option value="new">New</option>
                <option value="read">Read</option>
                <option value="resolved">Resolved</option>
                <option value="all">All</option>
              </Form.Select>
              <Badge bg="secondary">
                {contactMessages.length} message{contactMessages.length !== 1 ? 's' : ''}
              </Badge>
            </div>
          </Card.Body>
        </Card>

        {/* Messages List */}
        <Card className="shadow-sm">
          <Card.Body>
            {loadingContactMessages ? (
              <div className="text-center py-5">
                <Spinner animation="border" role="status">
                  <span className="visually-hidden">{t('common.loading')}</span>
                </Spinner>
                <p className="mt-2 mb-0">{t('inbox.loadingContactMessages')}</p>
              </div>
            ) : contactMessages.length === 0 ? (
              <div className="text-center py-5">
                <i className="bi bi-inbox" style={{ fontSize: '3rem', color: '#6c757d', marginBottom: '1rem' }}></i>
                <h5 className="mb-2">No Messages</h5>
                <p className="text-muted mb-0">
                  {statusFilter === 'all' 
                    ? 'No contact messages found.'
                    : `No ${statusFilter} contact messages found.`}
                </p>
              </div>
            ) : (
              <div style={{ maxHeight: '600px', overflowY: 'auto' }}>
                {contactMessages.map((msg) => (
                  <Card key={msg.id} className="mb-3">
                    <Card.Body>
                      <div className="d-flex justify-content-between align-items-start mb-2">
                        <div className="flex-grow-1">
                          <h6 className="mb-1">
                            {msg.userName}
                            <Badge 
                              bg={msg.status === 'new' ? 'primary' : msg.status === 'read' ? 'info' : 'success'} 
                              className="ms-2"
                            >
                              {msg.status}
                            </Badge>
                          </h6>
                          <small className="text-muted">{msg.userEmail}</small>
                        </div>
                        <small className="text-muted ms-2">
                          {new Date(msg.createdAt).toLocaleString()}
                        </small>
                      </div>
                      <div className="mt-2 mb-3" style={{ whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>
                        {msg.message}
                      </div>
                      <div className="d-flex gap-2 align-items-center">
                        <Form.Select
                          size="sm"
                          value={msg.status}
                          onChange={(e) => handleUpdateMessageStatus(msg.id, e.target.value)}
                          disabled={updatingMessageId === msg.id}
                          style={{ width: 'auto', minWidth: '120px' }}
                        >
                          <option value="new">New</option>
                          <option value="read">Read</option>
                          <option value="resolved">Resolved</option>
                        </Form.Select>
                        {updatingMessageId === msg.id && (
                          <Spinner animation="border" size="sm" />
                        )}
                        <Button
                          variant="outline-danger"
                          size="sm"
                          onClick={() => handleOpenDeleteModal(msg)}
                          disabled={deletingMessageId === msg.id}
                          className="ms-auto"
                        >
                          <i className="bi bi-trash me-1"></i>
                          Delete
                        </Button>
                      </div>
                    </Card.Body>
                  </Card>
                ))}
              </div>
            )}
          </Card.Body>
        </Card>
      </Container>

      {/* Delete Contact Message Modal */}
      <Modal 
        show={showDeleteMessageModal} 
        onHide={() => !deletingMessageId && setShowDeleteMessageModal(false)} 
        centered
      >
        <Modal.Header closeButton={!deletingMessageId}>
          <Modal.Title>Delete Contact Message</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <p className="mb-3">
            <strong>Are you sure you want to delete this contact message?</strong>
          </p>
          {messageToDelete && (
            <div className="mb-3">
              <p className="mb-1"><strong>From:</strong> {messageToDelete.userName} ({messageToDelete.userEmail})</p>
              <p className="mb-1"><strong>Date:</strong> {new Date(messageToDelete.createdAt).toLocaleString()}</p>
              <p className="mb-0"><strong>Message:</strong></p>
              <div className="p-2 bg-light rounded" style={{ whiteSpace: 'pre-wrap', wordBreak: 'break-word', maxHeight: '200px', overflowY: 'auto' }}>
                {messageToDelete.message}
              </div>
            </div>
          )}
          <p className="text-danger mb-0">
            This action cannot be undone.
          </p>
        </Modal.Body>
        <Modal.Footer>
          <Button
            variant="secondary"
            onClick={() => {
              setShowDeleteMessageModal(false);
              setMessageToDelete(null);
            }}
            disabled={deletingMessageId}
          >
            Cancel
          </Button>
          <Button
            variant="danger"
            onClick={handleDeleteMessage}
            disabled={deletingMessageId}
          >
            {deletingMessageId ? (
              <>
                <Spinner
                  as="span"
                  animation="border"
                  size="sm"
                  role="status"
                  aria-hidden="true"
                  className="me-2"
                />
                Deleting...
              </>
            ) : (
              <>
                <i className="bi bi-trash me-2"></i>
                Delete Message
              </>
            )}
          </Button>
        </Modal.Footer>
      </Modal>
      
      <VersionFooter />
    </div>
  );
}
